import { useState, forwardRef, useImperativeHandle, Ref } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { api } from '@/api/client';
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

// 表单数据类型定义
const userFormSchema = z.object({
    // name: z.string().min(2, "姓名至少2个字符"),
    email: z.string().email("请输入有效的邮箱地址"),
    type: z.string().min(1, "请选择邀请类型"),
    // phone: z.string().optional(),
});

type UserFormValues = z.infer<typeof userFormSchema>;

// 组件属性定义
interface AddUserDialogProps {
    // 新增成功后的回调
    onSuccess?: (response:any) => void;
}

// 暴露给父组件的方法类型
interface DialogRef {
    open: () => void;
}

// 模拟新增用户的API请求
const mockAddUserApi = async (userData: UserFormValues): Promise<{ success: boolean; message?: string }> => {
    // 模拟网络延迟
    return new Promise((resolve) => {
        setTimeout(() => {
            // 模拟随机成功/失败（实际项目中根据真实接口返回处理）
            if (userData.email.includes("test")) {
                resolve({ success: false, message: "该邮箱已被注册" });
            } else {
                resolve({ success: true });
            }
        }, 1500);
    });
};

export const AddKnowledge = forwardRef<DialogRef, AddUserDialogProps>(
    ({ onSuccess }, ref) => {
        // 控制弹窗显示状态
        const [isOpen, setIsOpen] = useState(false);
        // 控制提交加载状态
        const [isSubmitting, setIsSubmitting] = useState(false);
        // 错误信息
        const [errorMessage, setErrorMessage] = useState("");

        // 表单配置
        const form = useForm<UserFormValues>({
            resolver: zodResolver(userFormSchema),
            defaultValues: {
                email: "",
                type: "",
            },
        });

        // 暴露open方法给父组件
        useImperativeHandle(ref, () => ({
            open: () => {
                setIsOpen(true);
                form.reset(); // 打开时重置表单
                setErrorMessage(""); // 清空错误信息
            },
        }));

        // 处理表单提交
        const onSubmit = async (data: UserFormValues) => {
            try {
                setIsSubmitting(true);
                setErrorMessage("");
                console.log(data)
                // return false

                // 调用新增用户API
                const response:any = await api.post('/v1/admin/team/user/invite', {},{
                    params:{
                        ...data,
                    }
                });

                if (response.type) {
                    // 接口成功 - 关闭弹窗并触发回调
                    setIsOpen(false);
                    onSuccess?.(response);
                } else {
                    // 接口返回错误信息
                    setErrorMessage(response.message || "新增邀请用户失败，请重试");
                }
            } catch (error) {
                // 捕获网络等异常错误
                setErrorMessage("网络异常，请稍后重试");
            } finally {
                setIsSubmitting(false);
            }
        };

        return (
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>邀请成员</DialogTitle>
                        <DialogDescription>
                            请填写邀請成員信息，帶 * 的為必填項
                        </DialogDescription>
                    </DialogHeader>

                    {/* 错误提示 */}
                    {errorMessage && (
                        <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">
                            {errorMessage}
                        </div>
                    )}

                    {/* 用户表单 */}
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

                            <FormField
                                control={form.control}
                                name="email"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>邮箱 *</FormLabel>
                                        <FormControl>
                                            <Input placeholder="请输入邮箱" type="email" {...field} />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name="type"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>邀请类型 *</FormLabel>
                                        <Select
                                            onValueChange={field.onChange}
                                            defaultValue=""  // 显式设置默认值为空，触发校验
                                            value={field.value}  // 绑定表单字段值
                                        >
                                            <FormControl>
                                                <SelectTrigger>
                                                    <SelectValue placeholder="选择类型" />
                                                </SelectTrigger>
                                            </FormControl>
                                            <SelectContent>
                                                <SelectItem value="EMAIL">邮箱</SelectItem>
                                                <SelectItem value="LINK">链接</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />


                            <DialogFooter>
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => setIsOpen(false)}
                                    disabled={isSubmitting}
                                >
                                    取消
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? "提交中..." : "確認"}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        );
    }
);

AddKnowledge.displayName = "AddUserDialog";
